import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Loader2, Bot, User, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import api from '../../lib/api';

export default function EINAssistantModal({ isOpen, onClose, entityContext }) {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: entityContext 
        ? `Hi! I'm here to help you apply for an EIN for **${entityContext.name}**. Let's get started!\n\nFirst, let me confirm: Is this entity based in the United States?`
        : "Hi! I'm your EIN Application Assistant. I'll guide you through the process of obtaining an Employer Identification Number from the IRS.\n\nTo get started, what type of entity are you applying for? (LLC, Corporation, Trust, etc.)"
    }
  ]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestedActions, setSuggestedActions] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!userInput.trim() || loading) return;

    const newUserMessage = {
      role: 'user',
      content: userInput
    };

    setMessages(prev => [...prev, newUserMessage]);
    setUserInput('');
    setLoading(true);

    try {
      const response = await api.post('/ein/assistant', {
        conversation_history: messages,
        user_message: userInput,
        entity_context: entityContext ? {
          name: entityContext.name,
          type: entityContext.type,
          jurisdiction: entityContext.jurisdiction
        } : null
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.assistant_message
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      if (response.data.suggested_actions && response.data.suggested_actions.length > 0) {
        setSuggestedActions(response.data.suggested_actions);
      } else {
        setSuggestedActions([]);
      }

    } catch (error) {
      console.error('EIN Assistant error:', error);
      const errorMessage = {
        role: 'assistant',
        content: "I apologize, but I'm having trouble connecting right now. Please try again in a moment."
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-800 rounded-xl w-full max-w-3xl h-[600px] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white font-['Outfit']">EIN Application Assistant</h2>
              <p className="text-sm text-slate-400">Powered by AI • Conversational Guidance</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-5 h-5 text-white" />
                </div>
              )}
              
              <div
                className={`max-w-[80%] rounded-lg p-4 ${
                  msg.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-800 text-slate-200'
                }`}
              >
                <div className="text-sm leading-relaxed whitespace-pre-wrap">
                  {msg.content.split('**').map((part, i) => 
                    i % 2 === 0 ? part : <strong key={i} className="font-semibold">{part}</strong>
                  )}
                </div>
              </div>

              {msg.role === 'user' && (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-white" />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex gap-3 justify-start">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="bg-slate-800 rounded-lg p-4">
                <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggested Actions */}
        {suggestedActions.length > 0 && (
          <div className="px-6 pb-3">
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
              <p className="text-xs text-slate-400 mb-2 font-semibold">Suggested Next Steps:</p>
              <div className="flex flex-wrap gap-2">
                {suggestedActions.map((action, idx) => (
                  <button
                    key={idx}
                    className="text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 px-3 py-1.5 rounded-md flex items-center gap-1 transition-colors"
                    onClick={() => {
                      if (action.includes('IRS.gov') || action.includes('irs.gov')) {
                        window.open('https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online', '_blank');
                      } else if (action.includes('Form SS-4')) {
                        window.open('https://www.irs.gov/pub/irs-pdf/fss4.pdf', '_blank');
                      }
                    }}
                  >
                    {action}
                    {(action.includes('Visit') || action.includes('Download')) && (
                      <ExternalLink className="w-3 h-3" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-6 border-t border-slate-800">
          <div className="flex gap-3">
            <Input
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your question or response..."
              className="bg-slate-800 border-slate-700 focus:border-blue-500 h-12 text-white placeholder:text-slate-500"
              disabled={loading}
            />
            <Button
              onClick={handleSendMessage}
              disabled={!userInput.trim() || loading}
              className="gradient-accent px-6"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            This assistant provides guidance only. Always verify with the IRS directly.
          </p>
        </div>
      </div>
    </div>
  );
}
